import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import './ExchangeToFundEditor.scss';

import ExchangeMarketFluctuationInput from '../ExchangeMarketFluctuationInput/ExchangeMarketFluctuationInput';
import {
  getExchangeShowMarketFluctuationDisclosure,
  getExchangeFromSummaryFundList,
} from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { getPersonalInformationData } from '../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import Checkbox from '../../Shared/Checkbox/Checkbox';
import MultiInput from '../../Shared/MultiInput/MultiInput';
import RI2020DividendText from '../../Shared/RI2020DividendText/RI2020DividendText';
import {
  ExchangeType,
  UNIT,
  EARLY_WITHDRAWAL_PENALTY_AGE,
} from '../../../settings/settings';
import If from '../../Shared/If/If';
import Note from '../../Shared/Note/Note';
import { isAutoExchange } from '../../../utils/transactions-util';
import { getCurrentYear } from '../../../utils/dates/date-utility';
import { isAPP } from 'src/utils/advice/advice-utility';

/**
 * @typedef { import("../types").Fund } Fund
 */

const ALL_REMAINING_TEXT = 'All remaining';

const getShowRI2020 = (newFund, ticker) => {
  return (
    newFund &&
    (ticker === 'TRLAX' ||
      ticker === 'TRABX' ||
      ticker === 'TRAVX' ||
      ticker === 'TRATX')
  );
};

const ExchangeToFundEditor = ({
  actions: { updateExchangeFundAmount, updateExchangeFundUnit },
  exchangeFromUnit,
  exchangeShowMarketFluctuationDisclosure,
  exchangeType,
  fund,
  exchangeFromSummaryFundList,
  personalData,
}) => {
  /**
   * @type  Fund
   */
  const {
    agreementID,
    fundName,
    grossAmount,
    grossAmountError,
    grossUnit,
    isIRAContribution,
    newFund,
    ticker,
  } = fund;
  const isAll = grossUnit === UNIT.ALL_REMAINING;
  const showInput = exchangeType === ExchangeType.ONE_TO_MANY;
  const showRI2020 = getShowRI2020(newFund, ticker);

  const showFluctuation =
    exchangeShowMarketFluctuationDisclosure && isIRAContribution;
  const isRMD = personalData && personalData.eligibleForRMD;
  const isRetiredFund =
    exchangeFromSummaryFundList[0] &&
    exchangeFromSummaryFundList[0].displayClassification === 'RETIREMENT';
  const isRolloverOrTraditionalIRA =
    exchangeFromSummaryFundList[0] &&
    (exchangeFromSummaryFundList[0].agreementType === 'TRADITIONAL_IRA' ||
      exchangeFromSummaryFundList[0].agreementType === 'ROLLOVER_IRA');

  const autoExchangeEarlyWithdrawal =
    isRetiredFund &&
    fund.displayClassification !== 'RETIREMENT' &&
    isAutoExchange() &&
    personalData.age < EARLY_WITHDRAWAL_PENALTY_AGE;

  if (!isAutoExchange() && !(showInput || showRI2020 || showFluctuation)) {
    return null;
  }

  if (isAutoExchange() && !autoExchangeEarlyWithdrawal) {
    return null;
  }

  return (
    <div className="exchange-to-fund-editor-container">
      {showInput && (
        <Fragment>
          <div className="exchange-to-fund-editor-input-label">
            Enter an Amount:
          </div>
          <MultiInput
            allowedUnits={
              exchangeFromUnit === UNIT.DOLLARS || isAPP(fund.productType)
                ? [UNIT.DOLLARS, UNIT.PERCENT]
                : []
            }
            ariaLabel={`Exchange Amount for ${fundName}`}
            autoFocus={fund.selected}
            defaultValue={grossAmount}
            disabled={isAll}
            displayErrorMessage={grossAmountError}
            error={grossAmountError}
            errorClassName="exchange-to-fund-editor-error"
            inputID={`amt-${agreementID}`}
            onInputChange={(amount) =>
              updateExchangeFundAmount(agreementID, amount)
            }
            onUnitChange={(unit) => updateExchangeFundUnit(agreementID, unit)}
            selectedInput={isAll ? UNIT.PERCENT : grossUnit}
          />
          <span
            onClick={() =>
              updateExchangeFundUnit(
                agreementID,
                isAll ? UNIT.PERCENT : UNIT.ALL_REMAINING
              )
            }
            className="exchange-to-fund-editor-exchange-all-label"
          >
            <Checkbox
              ariaLabel={ALL_REMAINING_TEXT}
              checked={isAll}
              e2eSuffix="all-remaining"
              id={`exchange-all-${agreementID}`}
              onClick={() =>
                updateExchangeFundUnit(
                  agreementID,
                  isAll ? UNIT.PERCENT : UNIT.ALL_REMAINING
                )
              }
            />
            {ALL_REMAINING_TEXT}
          </span>
          <If
            test={
              isRMD &&
              isRetiredFund &&
              isRolloverOrTraditionalIRA &&
              fund.displayClassification !== 'RETIREMENT' &&
              parseInt(getCurrentYear('YYYY')) < 2021
            }
          >
            <Note title="Note:" stylename="exch-rmd-notes">
              The distribution you are requesting may help satisfy all or a
              portion of your required minimum distribution (RMD) for a given
              year. RMDs are waived in 2020 as a result of the CARES Act.
            </Note>
          </If>
        </Fragment>
      )}
      {showRI2020 && (
        <RI2020DividendText
          fund={fund}
          stylename="exchange-to-fund-editor-ri2020"
        />
      )}
      {showFluctuation && <ExchangeMarketFluctuationInput />}
      {autoExchangeEarlyWithdrawal && (
        <div>
          <div className="early-withdrawal-title">
            Early Withdrawal Penalty Warning
          </div>
          <div className="early-withdrawal-body">
            A 10% federal early withdrawal penalty may occur at tax time for
            taking out money before the age of 59.5
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { modalsSeen } = state.modals;
  return {
    exchangeShowMarketFluctuationDisclosure:
      getExchangeShowMarketFluctuationDisclosure(state),
    modalsSeen,
    exchangeFromSummaryFundList: getExchangeFromSummaryFundList(state),
    personalData: getPersonalInformationData(state),
  };
};

export default compose(connect(mapStateToProps))(ExchangeToFundEditor);
